import React, { Suspense, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@sparknz/spark-pattern-library';
import {
  LinkRegion,
  trackCustomLink,
  useTriggerableTrackPageView,
} from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
} from 'journey/productDetails/typings';
import {
  getProductDetailsPageViewParams,
  trackLinkedProductsEvent,
} from 'journey/productDetails/utils';
import { FallbackLoadingSpinner } from 'shared/components';
import { useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import {
  MpdChangePlanComparison,
  PostpaidChangePlanComparerField,
  SubscriptionChangePlanComparison,
} from './components';

const ReviewChangePlan = () => {
  const {
    dispatch,
    ifpEligibility: { isCheckRequired, isChangePlan, recommendations },
    selectedDevice,
    paymentOptions,
    planOffers,
  } = useProductDetails();
  const router = useRouter();
  const ifpId = extractFirstValue(router.query.ifpId);
  const planId = extractFirstValue(router.query.planId);
  const { isBusiness } = useSegment();
  const { trackPageView } = useTriggerableTrackPageView();

  useEffect(() => {
    if (isCheckRequired) {
      trackLinkedProductsEvent({
        device: selectedDevice,
        eventInfo: {
          eventName: 'Mobile Plan Selector Review View',
          eventAction: 'shop.mobilePlanSelector.review.view',
        },
        plan: planOffers?.find(({ id }) => id === planId),
        paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
        isBusiness,
        isChangePlan,
      });
    }
  }, [isCheckRequired]);

  const handleSubmit = () => {
    trackCustomLink('Save and continue');
    dispatch({
      type: ProductDetailsActionTypes.SetActionLayer,
      actionLayerStatus: ActionLayerStatus.Close,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.AddToCart,
      },
    });
    trackLinkedProductsEvent({
      device: selectedDevice,
      eventInfo: {
        eventName: 'Mobile Plan Selector Review Save',
        eventAction: 'shop.mobilePlanSelector.review.save',
      },
      plan: planOffers?.find(({ id }) => id === planId),
      paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
      isBusiness,
      isChangePlan,
      isSpotifyOptIn:
        recommendations && Object.values(recommendations)[0] === 'KEEP',
    });
    trackPageView(
      getProductDetailsPageViewParams({
        groupName: selectedDevice?.groupName,
        primaryCategory: selectedDevice?.primaryCategory,
      }),
    );
    trackLinkedProductsEvent({
      eventInfo: {
        eventName: 'Product View',
        eventAction: 'shop.prodViewAction',
      },
      device: selectedDevice,
      isBusiness,
      isChangePlan,
    });
  };

  return (
    <Suspense fallback={<FallbackLoadingSpinner />}>
      <PostpaidChangePlanComparerField />
      <MpdChangePlanComparison />
      <SubscriptionChangePlanComparison />
      <LinkRegion regionId="productDetails.changePlan.review.complete">
        <Button variant="primary" fullWidth onClick={handleSubmit}>
          Save and continue
        </Button>
      </LinkRegion>
    </Suspense>
  );
};

export default ReviewChangePlan;
